import {
  useGetAccountsForCareseeker,
  useLazyGetSSOIntegrationRights,
} from "apollo/hooks/queries";
import {
  PAGINATION_COUNT_20,
  PAGINATION_START_0,
  SORT_ORDER_ASC,
} from "core/consts";
import auth from "core/model/api/endpoints/auth";
import { formatUnixDate } from "core/model/utils/dates";
import { getErrorMessage } from "core/model/utils/errors";
import { getEmailDomain } from "core/model/utils/strings";
import {
  Account,
  Careseeker,
  GetAccountsForCareseekerQueryString,
  SortOrder,
  SSOIntegrationsRight,
} from "core/types";
import { ButtonProps } from "ds/ui";
import {
  CreateStepDataMap,
  useMultiStepConfirmationDialog,
} from "ds_legacy/components/ConfirmationDialog/useMultiStepConfirmationDialog";
import { dp } from "ds_legacy/materials/metrics";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useState } from "react";
import { useLoggedInAccount, useOnLogout } from "reduxentities/hooks";
import { Locale, useTranslations } from "translations";
import { SSODialogContent, SSOHeaderIcon } from "./SSODialogContent";
import { useCreateAccountForCareseeker } from "./useCreateAccountForCareseeker";
import { useAccountByEmailHandler } from "./useGetAccountByEmail";
import { useUpdateAccountForCareseeker } from "./useUpdateAccountForCareseeker";

// Define step data types for SSO activation flow
type SSOActivationStepData = CreateStepDataMap<{
  activation: { activated: boolean };
  consent: {
    cancelButtonProps?: Omit<ButtonProps<false>, "onPress" | "ref">;
    cancelText?: string;
    confirmButtonProps?: Omit<ButtonProps<false>, "onPress" | "ref">;
    confirmed: boolean;
    content?: React.ReactNode;
    hasRightsForAll?: boolean;
    headerIcon?: React.ReactNode;
    ssoIntegrationsRight?: SSOIntegrationsRight[];
    submitText?: string;
    title?: string;
  };
}>;

export type AccountDialogState = {
  account?: Account;
  error?: string;
  isOnlyActiveUserManager?: boolean;
  isOtherCareseekerAccount?: boolean;
  prevStep?: Step | null;
  step: Step | null;
};

export type GetAccountsForCareseekerFilters = {
  count: number;
  search_term: string;
  sort_field: string;
  sort_order: SortOrder;
  start: number;
};

type Step = (typeof _STEPS)[number];

export const STEP_1 = 1;
export const STEP_2 = 2;
const _STEPS = [STEP_1, STEP_2] as const;

// don't change this unless you change it in the BE too
export const SSO_ACTIVATION_SUCCESS_PARAM = "isActivation";

export function buildQueryString(
  filters: GetAccountsForCareseekerFilters,
): GetAccountsForCareseekerQueryString {
  return `search_term=${encodeURIComponent(filters.search_term)}&start=${
    filters.start
  }&count=${filters.count}&sort_field=${filters.sort_field}&sort_order=${
    filters.sort_order
  }`;
}

export function getTimestamp(locale: Locale) {
  return encodeURIComponent(
    formatUnixDate(new Date().getTime() / 1000, locale, {
      customFormat: "dd.MM.yyyy HH:mm",
    }),
  );
}
export const initialPagination = {
  count: PAGINATION_COUNT_20,
  start: PAGINATION_START_0,
} as const;

export function useUserManagementFeature({
  careseeker,
}: {
  careseeker: Careseeker | undefined;
}) {
  const loggedAccount = useLoggedInAccount();
  const translations = useTranslations();
  const toast = useToast();
  const [filtersState, setFiltersState] =
    useState<GetAccountsForCareseekerFilters>({
      search_term: "",
      ...initialPagination,
      sort_field: "status",
      sort_order: SORT_ORDER_ASC,
    });
  const filters = buildQueryString(filtersState);

  const [accountDialog, setAccountDialog] = useState<AccountDialogState>({
    account: undefined,
    step: null,
  });

  const [accountsProgress, accountsPayload] = useGetAccountsForCareseeker({
    fetchPolicy: "network-only",
    variables: { careseekerId: careseeker?.id || -1, filters },
  });

  const getAccountProps = useAccountByEmailHandler({
    careseeker,
    setAccountDialog,
  });

  const createAccountProps = useCreateAccountForCareseeker({
    careseeker,
    setAccountDialog,
  });

  const updateAccountProps = useUpdateAccountForCareseeker({
    careseeker,
    setAccountDialog,
  });

  const [fetchSSOIntegrationRights] = useLazyGetSSOIntegrationRights();

  const {
    confirmMultiStep,
    props: ssoMultiStepProps,
    setLoading,
  } = useMultiStepConfirmationDialog<SSOActivationStepData>();

  const onLogout = useOnLogout();

  const handleActivateSSO = async () => {
    await confirmMultiStep([
      {
        cancelText: translations.userManagement.ssoFlow.enableLaterButton,
        confirmButtonProps: { id: "sso-consent" },
        content: (
          <p className="text-gray-600">
            {translations.userManagement.ssoFlow.consentInformation}
          </p>
        ),
        headerIcon: <SSOHeaderIcon variant="info" />,
        id: "consent",
        onStepSubmit: async (_data?: SSOActivationStepData) => {
          setLoading(true);

          try {
            const domain = getEmailDomain(loggedAccount?.email);
            if (!domain) {
              throw new Error(
                "Unable to extract email domain for SSO activation. Please ensure you have a valid email address.",
              );
            }

            const ssoIntegrationsRight = await fetchSSOIntegrationRights({
              domain,
            });

            if (!ssoIntegrationsRight || ssoIntegrationsRight.length === 0) {
              throw new Error(
                "No SSO integration rights found for this domain.",
              );
            }

            // Check if user has admin rights for all senders
            const hasRightsForAll = ssoIntegrationsRight.every(
              (integration) => integration.has_admin_rights,
            );

            return {
              ...(hasRightsForAll
                ? {}
                : {
                    cancelButtonProps: {
                      color: "primary",
                      id: "sso-understood",
                      variant: "solid",
                    },
                    cancelText:
                      translations.userManagement.ssoFlow.stepTwo
                        .understandButton,
                    submitText: null, // null to hide the button
                  }),
              content: (
                <SSODialogContent
                  headerNotUserManager={
                    hasRightsForAll
                      ? undefined
                      : translations.userManagement.ssoFlow.stepTwo
                          .headerNotUserManager
                  }
                  headerUserManager={
                    hasRightsForAll
                      ? undefined
                      : translations.userManagement.ssoFlow.stepTwo
                          .headerUserManagerRights
                  }
                  integrations={ssoIntegrationsRight}
                  showContactSupport={!hasRightsForAll}
                  subtitle={
                    hasRightsForAll
                      ? translations.userManagement.ssoFlow.stepTwo.subtitle
                      : translations.userManagement.ssoFlow.stepTwo
                          .issueSubtitle
                  }
                />
              ),
              hasRightsForAll,
              headerIcon: hasRightsForAll ? undefined : (
                <SSOHeaderIcon variant="warning" />
              ),
              ssoIntegrationsRight,
              title: hasRightsForAll
                ? translations.userManagement.ssoFlow.stepTwo.title
                : translations.userManagement.ssoFlow.stepTwo.issueTitle,
            };
          } catch (error) {
            toast({ color: "danger", message: translations.general.error });
            console.error("SSO activation failed:", getErrorMessage(error));
          } finally {
            // Clear loading state
            setLoading(false);
          }
        },
        submitText: translations.userManagement.ssoFlow.consentButton,
        sx: { maxWidth: dp(425) },
        title: translations.userManagement.ssoFlow.consentModalHeader,
      },
      {
        cancelText: translations.actions.cancel,
        confirmButtonProps: { id: "sso-activate" },
        headerIcon: <SSOHeaderIcon variant="info" />,
        id: "activation",
        onStepSubmit: async (data?: SSOActivationStepData) => {
          const consentData = data?.["consent"];
          const { hasRightsForAll } = consentData || {};

          if (!hasRightsForAll) {
            return false;
          }

          setLoading(true);
          try {
            if (!loggedAccount?.email) {
              throw new Error("No email address available for SSO activation");
            }

            const redirectBase = new URL(window.location.href);
            redirectBase.searchParams.set(SSO_ACTIVATION_SUCCESS_PARAM, "true");

            const res = await auth.oauthCheck({
              email: loggedAccount.email,
              is_user_manager: true,
              redirect_to_url: btoa(redirectBase.toString()),
            });

            const ssoUrl = res?.url;
            if (ssoUrl) {
              // Logout current user before redirecting to SSO
              await onLogout();

              window.location.href = ssoUrl;
              return true;
            } else {
              throw new Error(
                "This email is not eligible for SSO authentication",
              );
            }
          } catch (ssoError) {
            toast({
              color: "danger",
              message: translations.general.error,
            });
            console.error("SSO activation failed:", getErrorMessage(ssoError));
            return false;
          }
        },
        submitText: translations.userManagement.ssoActivationButton,
        sx: { maxWidth: dp(425) },
      },
    ]);
  };

  return {
    ...accountsPayload,
    ...createAccountProps,
    ...getAccountProps,
    ...updateAccountProps,
    accountDialog,
    accountsProgress,
    filtersState,
    handleActivateSSO,
    setAccountDialog,
    setFiltersState,
    ssoMultiStepProps,
  };
}
